import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { HelperService } from '../../../services/helper/helper.service';
import { faBell } from '@fortawesome/free-regular-svg-icons/faBell';
import { faClock } from '@fortawesome/free-regular-svg-icons/faClock';
import { BuddyInstanceInterface } from '../../../models/interface/buddy/buddy-instance.interface';
import { UserInterface } from '../../../models/interface/user.interface';
import { BehaviorSubject, filter, Observable, skip, Subscription, take } from 'rxjs';
import { BuddyStore } from '../../../store/buddy/component-store/buddy.store';
import { HttpResponse } from '@angular/common/http';
import { HelperActivityService } from '../../../services/helper/helper-activity/helper-activity.service';
import { BuddyActivityInterface } from '../../../models/interface/buddy/buddy-activity.interface';

type PagedActivity = {
  pair1: BuddyActivityInterface;
  pair2: BuddyActivityInterface;
  localizedDate: string;
};

export type DialogBuddyInstanceDeleteData = {
  buddyInstance: BuddyInstanceInterface;
  members: UserInterface[];
  studyCollaborators: UserInterface[];
  studyId: number;
};

@Component({
  selector: 'app-dialog-buddy-instance-delete',
  templateUrl: './dialog-buddy-instance-delete.component.html',
  styleUrls: ['./dialog-buddy-instance-delete.component.css'],
  providers: [BuddyStore],
})
export class DialogBuddyInstanceDeleteComponent implements OnDestroy, OnInit {
  // Icons
  faBell = faBell;
  faClock = faClock;

  public buddyInstance: BuddyInstanceInterface;
  public members: UserInterface[] = [];
  public studyCollaborators: UserInterface[] = [];

  public activities: BuddyActivityInterface[] = [];
  public pagedActivitySubject = new BehaviorSubject<PagedActivity[]>([]);
  public pagedActivity$ = this.pagedActivitySubject.asObservable();

  public buddyActivities$: Observable<BuddyActivityInterface[]>;

  public buddyActivitiesPair: PagedActivity[] = [];

  public isLoadingActivities$: Observable<boolean>;

  public deleteBuddyResponse = new BehaviorSubject<'DEFAULT' | 'LOADING' | 'SUCCESS' | 'FAILURE'>('DEFAULT');

  public studyId: number;

  private isLoadingActivitiesSubject = new BehaviorSubject<boolean>(true);

  private subscriptions: Subscription[] = [];

  private deleteBuddyResponse$: Observable<any>;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: DialogBuddyInstanceDeleteData,
    private dialogRef: MatDialogRef<DialogBuddyInstanceDeleteComponent>,
    private buddyStore: BuddyStore,
    private helperService: HelperService,
    private helperActivityService: HelperActivityService,
  ) {
    this.isLoadingActivities$ = this.isLoadingActivitiesSubject.asObservable();
    this.buddyActivities$ = this.buddyStore.allBuddyActivitiesOfBuddy$;
    this.deleteBuddyResponse$ = this.buddyStore.deleteBuddyResponse$;
  }

  public get helper() {
    return this.helperService;
  }

  public get helperActivity() {
    return this.helperActivityService;
  }

  ngOnInit(): void {
    this.buddyInstance = this.data.buddyInstance;
    this.members = this.data.members;
    this.studyCollaborators = this.data.studyCollaborators;
    this.studyId = this.data.studyId;

    this.buddyStore.getBuddyActivities(this.buddyInstance.id);
    this.subscriptions.push(
      this.buddyActivities$.pipe(skip(1), take(1)).subscribe({
        next: (result) => {
          this.activities = result;
          this.activities = this.helper.getBuddyActivityByNewest(this.activities);
          this.pairBuddy();
          this.pagedActivitySubject.next(this.buddyActivitiesPair.slice(0, 20));
          this.isLoadingActivitiesSubject.next(false);
        },
        error: () => {
          this.activities = [];
          this.pagedActivitySubject.next([]);
          this.isLoadingActivitiesSubject.next(false);
        },
      })
    );
  }

  public updatePagedActivities(event: PagedActivity[]) {
    if (event) {
      this.pagedActivitySubject.next(event);
    }
  }

  public hasSentReminder(buddyActivityPair: PagedActivity): boolean {
    const reminder1 = buddyActivityPair?.pair1?.attributes.daily_reminder_timestamps;
    const reminder2 = buddyActivityPair?.pair2?.attributes.daily_reminder_timestamps;
    return !!reminder1 || !!reminder2;
  }

  public getBuddyState(buddyActivityPair: PagedActivity) {
    const task1 = buddyActivityPair?.pair1?.attributes.daily_task_timestamps;
    const task2 = buddyActivityPair?.pair2?.attributes.daily_task_timestamps;
    if (!!task1 && !!task2) {
      return 'both_daily_task_completed';
    } else if (!!task1 || !!task2) {
      return 'only_one_daily_task_completed';
    } else {
      return 'no_daily_task_completed';
    }
  }

  getClassForActivity(activity: PagedActivity) {
    const state = this.getBuddyState(activity);
    switch (state) {
      case 'no_daily_task_completed':
        return 'text-danger';
      case 'only_one_daily_task_completed':
        return 'text-info';
      case 'both_daily_task_completed':
        return 'text-success';
    }
  }

  public getBuddySingleState(pair: BuddyActivityInterface) {
    const task = pair.attributes.daily_task_timestamps;
    const reminder = pair.attributes.daily_reminder_timestamps;
    if (!!task && !!reminder) {
      return 'one_daily_task_completed_with_reminder';
    } else if (!!task) {
      return 'one_daily_task_completed';
    } else {
      return 'no_daily_task_completed';
    }
  }

  getClassForSingleActivity(activity: BuddyActivityInterface) {
    const state = this.getBuddySingleState(activity);
    switch (state) {
      case 'no_daily_task_completed':
        return 'text-danger';
      case 'one_daily_task_completed':
      case 'one_daily_task_completed_with_reminder':
        return 'text-success';
    }
  }

  public pairBuddy(): void {
    const buddyActivitiesPair: PagedActivity[] = [];
    this.activities.forEach((activity) => {
      const localizeDate = this.helper.localizeDate(activity.attributes.created_at);
      if (!buddyActivitiesPair.find((pair) => pair.localizedDate.toString() === localizeDate)) {
        const buddyAct = this.activities.find(
          (buddyActivity) =>
            buddyActivity.attributes.user_id.toString() !== activity.attributes.user_id.toString() &&
            this.helper.localizeDate(buddyActivity.attributes.created_at) === localizeDate,
        );
        buddyActivitiesPair.push({
          pair1: activity,
          pair2: buddyAct,
          localizedDate: localizeDate,
        });
      }
    });
    this.buddyActivitiesPair = buddyActivitiesPair;
  }

  private getMissingUser(availableUser: number) {
    const user0 = this.buddyInstance.attributes.buddies_ids[0];
    const user1 = this.buddyInstance.attributes.buddies_ids[1];
    let buddy = 'System';
    if (availableUser == user0) {
      if (this.helper.findArrObjById(user1, this.members) !== null) {
        buddy = this.helper.getCodeNameEmail(user1, this.members);
      }
    } else if (availableUser == user1) {
      if (this.helper.findArrObjById(user0, this.members) !== null) {
        buddy = this.helper.getCodeNameEmail(user0, this.members);
      }
    }
    return { buddy };
  }

  getMissingUserPair(activity: BuddyActivityInterface) {
    const availableUser = activity?.attributes?.user_id;
    if (!availableUser) {
      return { buddy: 'System' };
    }
    return this.getMissingUser(availableUser);
  }

  public deleteBuddyInstance(buddyInstance: BuddyInstanceInterface): void {
    if (this.deleteBuddyResponse.value === 'DEFAULT') {
      this.deleteBuddyResponse.next('LOADING');
      this.buddyStore.deleteBuddy(buddyInstance.id);
      this.subscriptions.push(
        this.deleteBuddyResponse$
          .pipe(
            filter((value) => !!value),
            take(1),
          )
          .subscribe((result) => {
            if (result instanceof HttpResponse) {
              this.deleteBuddyResponse.next('SUCCESS');
              setTimeout(() => {
                this.dialogRef.close();
              }, 2500);
            } else {
              this.deleteBuddyResponse.next('FAILURE');
              setTimeout(() => {
                this.deleteBuddyResponse.next('DEFAULT');
              }, 2500);
            }
          }),
      );
    }
  }

  public getTranslation(userId?: number): { buddy: string } {
    if (userId === undefined || this.helper.findArrObjById(userId, this.members) === null) {
      return { buddy: 'System' };
    }
    return { buddy: this.helper.getCodeNameEmail(userId, this.members) };
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
}
